import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { TablerIconsModule } from 'angular-tabler-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  merge,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { LogoutService } from 'src/app/auth/logout.service';
import {
  PAGE_SIZE_OPTIONS,
  PROJECT_TITLE,
} from 'src/app/common/constant/constants.type';
import { MaterialModule } from 'src/app/material.module';
import { PaginationRequest } from 'src/app/models/common.request';
import { PaginationResult } from 'src/app/models/common.response';
import { AppointmentDialog } from '../dialogs/confirmation-appointment/appointment-dialog.component';
import { HospitalDetailComponent } from './detail/hospital-detail.component';
import {
  HospitalService,
  MigrateLegacyDocumentsResult,
} from './hospital.service';
import { HospitalSummary, IHospitalInfo, LegacyDocumentOperationView } from './ihospital-info';

const SEARCH_DEBOUNCE_MS = 300;
const FIRST_PAGE = 0;
const DEFAULT_PAGE_SIZE = 10;

type LegacyDocumentOperation = 'copy' | 'delete';

// SuperAdmin console, phase 1: list the hospitals on the platform and onboard a new
// one. Nothing else is exposed - a SuperAdmin has no HospitalId, so the tenant-scoped
// screens (dashboard, OPD, billing) have no meaningful data to show.
@Component({
  selector: 'app-hospital',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    TablerIconsModule,
    TranslateModule,
  ],
  templateUrl: './hospital.component.html',
  styleUrl: './hospital.component.scss',
})
export class HospitalComponent implements OnInit, OnDestroy {
  readonly displayedColumns: string[] = [
    'name',
    'registrationNumber',
    'city',
    'mobile',
    'status',
    'abdm',
    'ipd',
    'pharmacy',
    'whatsApp',
    'documentActions',
    'actions',
  ];
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly projectTitle = PROJECT_TITLE;

  dataSource = new MatTableDataSource<IHospitalInfo>();
  pagination: PaginationResult | null = null;
  summary: HospitalSummary | null = null;
  searchControl = new FormControl('');
  statusControl = new FormControl('all', { nonNullable: true });
  cityControl = new FormControl('all', { nonNullable: true });
  abdmControl = new FormControl('all', { nonNullable: true });
  pharmacyControl = new FormControl('all', { nonNullable: true });
  isLoading = false;
  activeDocumentOperation: {
    hospitalId: number;
    type: LegacyDocumentOperation;
  } | null = null;
  documentOperationResult: LegacyDocumentOperationView | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private hospitalService: HospitalService,
    private logoutService: LogoutService,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.configureTableFilter();
    this.watchHospitals();
    this.watchSearch();
    this.watchFilters();
    this.loadHospitals(FIRST_PAGE);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addHospital(): void {
    this.dialog
      .open(HospitalDetailComponent, {
        autoFocus: false,
        position: { top: '0', right: '0' },
        height: '100vh',
        width: '82vw',
        maxWidth: '1400px',
        panelClass: 'side-panel',
        disableClose: true,
      })
      .afterClosed()
      .subscribe(() => {
        this.loadHospitals(FIRST_PAGE);
      });
  }

  editHospital(hospital: IHospitalInfo): void {
    this.dialog.open(HospitalDetailComponent, {
      autoFocus: false,
      position: { top: '0', right: '0' },
      height: '100vh',
      width: '82vw',
      maxWidth: '1400px',
      panelClass: 'side-panel',
      disableClose: true,
      data: hospital,
    }).afterClosed().pipe(take(1), takeUntil(this.destroy$)).subscribe(saved => {
      if (saved) {
        this.loadHospitals(this.pagination?.currentPage ?? FIRST_PAGE);
      }
    });
  }

  confirmLegacyDocumentCopy(hospital: IHospitalInfo): void {
    this.confirmDocumentOperation(
      this.translate.instant('HOSPITAL_ADMIN.COPY_DIALOG_TITLE'),
      this.translate.instant('HOSPITAL_ADMIN.COPY_DIALOG_MESSAGE', {
        hospitalName: hospital.name,
      }),
      this.translate.instant('HOSPITAL_ADMIN.COPY_DIALOG_CONFIRM'),
      () => this.copyLegacyDocuments(hospital),
    );
  }

  isDocumentOperationRunning(
    hospitalId: number,
    type: LegacyDocumentOperation,
  ): boolean {
    return (
      this.activeDocumentOperation?.hospitalId === hospitalId &&
      this.activeDocumentOperation.type === type
    );
  }

  get isAnyDocumentOperationRunning(): boolean {
    return this.activeDocumentOperation !== null;
  }

  private confirmDocumentOperation(
    title: string,
    message: string,
    confirmText: string,
    onConfirm: () => void,
  ): void {
    this.dialog
      .open(AppointmentDialog, {
        width: '520px',
        disableClose: true,
        data: {
          title,
          message,
          cancelText: this.translate.instant('HOSPITAL_ADMIN.CANCEL'),
          confirmText,
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((result) => result === 'yes'),
        takeUntil(this.destroy$),
      )
      .subscribe(onConfirm);
  }

  onPageChange(event: PageEvent): void {
    this.loadHospitals(event.pageIndex, event.pageSize);
  }

  logout(): void {
    this.logoutService.logout();
  }

  get totalHospitals(): number {
    if (this.summary) return this.summary.active + this.summary.inactive;
    return this.pagination?.rowCount ?? this.dataSource.data.length;
  }

  get cityOptions(): string[] {
    return [
      ...new Set(
        this.dataSource.data.map((hospital) => hospital.city).filter(Boolean),
      ),
    ].sort((a, b) => a.localeCompare(b));
  }

  resetFilters(): void {
    this.statusControl.setValue('all', { emitEvent: false });
    this.cityControl.setValue('all', { emitEvent: false });
    this.abdmControl.setValue('all', { emitEvent: false });
    this.pharmacyControl.setValue('all', { emitEvent: false });
    this.searchControl.setValue('');
    this.applyTableFilter();
  }

  private copyLegacyDocuments(hospital: IHospitalInfo): void {
    this.startDocumentOperation(hospital.id, 'copy');

    this.hospitalService
      .migrateLegacyDocuments(hospital.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.finishDocumentOperation()),
      )
      .subscribe({
        next: (response) =>
          response.isSuccess
            ? this.showCopyResult(hospital, response.result)
            : this.showOperationError(response.errors, response.message),
        error: () =>
          this.showOperationError(
            [],
            this.translate.instant('HOSPITAL_ADMIN.DOCUMENT_COPY_FAILED'),
          ),
      });
  }

  private startDocumentOperation(
    hospitalId: number,
    type: LegacyDocumentOperation,
  ): void {
    this.activeDocumentOperation = { hospitalId, type };
    this.documentOperationResult = null;
  }

  private finishDocumentOperation(): void {
    this.activeDocumentOperation = null;
  }

  private showCopyResult(
    hospital: IHospitalInfo,
    result: MigrateLegacyDocumentsResult,
  ): void {
    this.documentOperationResult = {
      hospitalName: hospital.name,
      title: this.translate.instant('HOSPITAL_ADMIN.COPY_RESULT_TITLE'),
      found: result.legacyRowsFound,
      completed: result.migrated,
      missing: result.missingFile,
      failed: result.failed,
      errors: result.errors,
    };
    this.showResultToast(result.migrated, result.failed, result.errors);
  }

  private showResultToast(
    completed: number,
    failed: number,
    errors: string[],
  ): void {
    if (failed > 0 || errors.length > 0) {
      this.toastr.warning(
        this.translate.instant('HOSPITAL_ADMIN.OPERATION_COUNTS', {
          completed,
          failed,
        }),
        this.translate.instant(
          'HOSPITAL_ADMIN.OPERATION_COMPLETED_WITH_FAILURES',
        ),
      );
      return;
    }

    this.toastr.success(
      this.translate.instant('HOSPITAL_ADMIN.COMPLETED_COUNT', { completed }),
      this.translate.instant('HOSPITAL_ADMIN.OPERATION_COMPLETED'),
    );
  }

  private showOperationError(errors: string[], message: string): void {
    this.toastr.error(errors.length > 0 ? errors.join('\n') : message);
  }

  private watchHospitals(): void {
    this.hospitalService.hospitals$
      .pipe(takeUntil(this.destroy$))
      .subscribe((hospitals) => {
        this.dataSource.data = hospitals;
        this.applyTableFilter();
      });

    this.hospitalService.pagination$
      .pipe(takeUntil(this.destroy$))
      .subscribe((pagination) => {
        this.pagination = pagination;
      });
  }

  private watchSearch(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.loadHospitals(FIRST_PAGE);
      });
  }

  private watchFilters(): void {
    merge(
      this.statusControl.valueChanges,
      this.cityControl.valueChanges,
      this.abdmControl.valueChanges,
      this.pharmacyControl.valueChanges,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyTableFilter());
  }

  private configureTableFilter(): void {
    this.dataSource.filterPredicate = (hospital, rawFilter) => {
      const selected = JSON.parse(rawFilter) as {
        status: string;
        city: string;
        abdm: string;
        pharmacy: string;
      };
      const statusMatches =
        selected.status === 'all' ||
        (selected.status === 'active'
          ? hospital.isActive !== false
          : hospital.isActive === false);
      const cityMatches =
        selected.city === 'all' || hospital.city === selected.city;
      const abdmMatches =
        selected.abdm === 'all' ||
        String(!!hospital.isAbdmEnabled) === selected.abdm;
      const pharmacyMatches =
        selected.pharmacy === 'all' ||
        String(!!hospital.isPharmacyEnabled) === selected.pharmacy;
      return statusMatches && cityMatches && abdmMatches && pharmacyMatches;
    };
  }

  private applyTableFilter(): void {
    this.dataSource.filter = JSON.stringify({
      status: this.statusControl.value,
      city: this.cityControl.value,
      abdm: this.abdmControl.value,
      pharmacy: this.pharmacyControl.value,
    });
  }

  private loadHospitals(pageNumber: number, pageSize?: number): void {
    this.isLoading = true;

    const request: PaginationRequest = {
      pageNumber: pageNumber,
      pageSize: pageSize || this.pagination?.pageSize || DEFAULT_PAGE_SIZE,
      isDescending: false,
      searchValue: this.searchControl.value?.trim() || '',
    };

    this.hospitalService
      .getHospital(request)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe(response => {
        this.summary = response.result.summary ?? null;
      });
  }
}
